import { BellIcon, Cross2Icon, PersonIcon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { ArrowRightLeft, Loader2, MoreVerticalIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useUser } from '@/features/auth/hooks/use-user';
import { useNotificationHubFilterStore } from '@/features/notifications/components/notification-hub-filter';
import { useOrgUser } from '@/features/org/hooks/use-org-user';
import { OrgRole } from '@/features/org/validation/org-role.schema';
import { ProjectDetailsTrackingReportHistory } from '@/features/projects/kpi-tracking-entry/components/project-details-tracking-report-history';
import { ProjectDetailsHeader } from '@/features/projects/project-details/components/layouts/project-details-header-layout';
import { ProjectComments } from '@/features/projects/project-details/components/pages/project-details-page/project-comments';
import { ProjectDetailsCharts } from '@/features/projects/project-details/components/pages/project-details-page/project-details-charts';
import { ProjectDetailsDocuments } from '@/features/projects/project-details/components/pages/project-details-page/project-details-documents';
import { ProjectDetailsKpis } from '@/features/projects/project-details/components/pages/project-details-page/project-details-kpis';
import { ProjectDetailsSubProjects } from '@/features/projects/project-details/components/pages/project-details-page/project-details-sub-projects';
import { ProjectNotes } from '@/features/projects/project-details/components/pages/project-details-page/project-notes';
import { ProjectPropertiesMenu } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-menu';
import { ProjectOwnershipTransferDialog } from '@/features/projects/project-details/components/project-ownership-transfer-dialog';
import { useProjectKpisWithMetrics } from '@/features/projects/project-details/hooks/use-project-kpis';
import { useUpdateProjectDescription } from '@/features/projects/project-details/hooks/use-update-project-description';
import { useUpdateProjectName } from '@/features/projects/project-details/hooks/use-update-project-name';
import { ProjectDetails } from '@/features/projects/shared/api/get-project-details';
import { useDeleteProjectMutation } from '@/features/projects/shared/hooks/use-delete-project-mutation';
import { useProjectContractors } from '@/features/projects/shared/hooks/use-project-contractors';
import { useProjectDetails } from '@/features/projects/shared/hooks/use-project-details';
import { useProjectMembersQuery } from '@/features/projects/shared/hooks/use-project-members-query';
import { isDerivativeKpi } from '@/features/projects/shared/utils/kpi-utils';
import { ProjectPlansPopover } from '@/features/subscriptions/components/project-plans-dialog';
import DeleteIcon from '@/shared/assets/delete.svg?react';
import EditIcon from '@/shared/assets/edit.png';
import EyeIcon from '@/shared/assets/eye.png';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { CollapsibleTriggerContainer } from '@/shared/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { toast } from '@/shared/hooks/use-toast';
import { cn } from '@/shared/utils/cn';

export const ProjectDetailsPage = () => {
  const { projectId } = useParams();
  const { data: orgUser } = useOrgUser();
  const { data: user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const { t: transferT } = useTranslation('project', {
    keyPrefix: 'ownership_transfer',
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const orgRole = orgUser?.role as OrgRole;
  const { setProjectIds, setSubprojectIds, reset } =
    useNotificationHubFilterStore();

  const { mutate: deleteProject, isPending: isDeleting } =
    useDeleteProjectMutation({
      onSuccess: () => {
        toast({ title: t('delete_project.success') });
        navigate(paths.projects.hub.getHref());
      },
      onError: () => {
        toast({
          title: t('delete_project.error'),
          variant: 'destructive',
        });
      },
    });

  if (!projectId) {
    navigate(paths.projects.hub.getHref());
    return null;
  }

  const { isLoading, data: project } = useProjectDetails({
    projectId: projectId,
  });

  const { data: projectMembers } = useProjectMembersQuery({
    projectId: projectId!,
  });

  const isProjectMemberWithMemberRole = useMemo(() => {
    if (!user?.id || !projectMembers) return false;
    return projectMembers.some(
      (member) =>
        member.org_member.user_id === user.id &&
        member.org_member.role === OrgRole.MEMBER,
    );
  }, [user?.id, projectMembers]);

  const shouldShowManageAccessButton =
    orgRole === OrgRole.OWNER ||
    orgRole === OrgRole.ADMIN ||
    !isProjectMemberWithMemberRole;

  const handleDeleteProject = () => {
    deleteProject({ id: parseInt(projectId) });
  };

  const handleBellClick = () => {
    if (!project) return;

    reset();

    // Set filter based on project type
    if (
      project.type === 'subproject_client' ||
      project.type === 'subproject_contractor'
    ) {
      if (project.parent_id) {
        setProjectIds([project.parent_id!.toString()]);
      }
      setSubprojectIds([project.id.toString()]);
    } else {
      setProjectIds([project.id.toString()]);
      setSubprojectIds([]);
    }

    // Navigate to notifications hub
    navigate(paths.notifications.getHref());
  };

  return (
    <DefaultLayout
      navigationActionRowClassName="mb-0"
      headerActionsPosition="top"
      headerActions={[
        project && <ProjectPlansPopover project={project} />,
        shouldShowManageAccessButton && (
          <Button
            key="manage-access"
            variant="greenGradient"
            btnType="secondary"
            onClick={() =>
              navigate(
                paths.projects.details.memberManagement.getHref(projectId),
              )
            }
          >
            <PersonIcon />
            {t('manage_access')}
          </Button>
        ),
        <Button
          shape="square"
          variant={'outline'}
          btnType="secondary"
          onClick={handleBellClick}
        >
          <BellIcon className="stroke-white" />
        </Button>,
        (orgRole === OrgRole.OWNER || orgRole === OrgRole.ADMIN) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'outline'} shape="square" btnType="secondary">
                <MoreVerticalIcon className="stroke-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {project?.type === 'project' && (
                <DropdownMenuItem onClick={() => setIsTransferDialogOpen(true)}>
                  <ArrowRightLeft className="size-4" />
                  {transferT('action')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setIsDeleteDialogOpen(true)}
                className="text-red-400 focus:text-red-400"
              >
                <DeleteIcon className="size-4" />
                {t('delete_project.action')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      ]}
    >
      <div className="mb-8 flex flex-1 flex-col gap-20">
        {isLoading ? (
          <LoadingIndicator className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform" />
        ) : project ? (
          <ProjectDetailsLoaded project={project} />
        ) : (
          <ProjectError />
        )}
      </div>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('delete_project.title', { name: project?.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_project.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={isDeleting}
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t('delete_project.cancel')}
            </Button>
            <Button
              onClick={handleDeleteProject}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('delete_project.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {project && (
        <ProjectOwnershipTransferDialog
          projectId={project.id}
          projectName={project.name}
          isOpen={isTransferDialogOpen}
          onOpenChange={setIsTransferDialogOpen}
        />
      )}
    </DefaultLayout>
  );
};

const ProjectDetailsLoaded = ({ project }: { project: ProjectDetails }) => {
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });

  const [isPropertiesExpanded, toggleProperties] = useState(false);
  const [isNotesExpanded, toggleNotes] = useState(false);
  const [activeTab, setActiveTab] = useState<'team' | 'private'>('team');
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [editedName, setEditedName] = useState(project.name);
  const [isEditDescriptionOpen, setIsEditDescriptionOpen] = useState(false);
  const [editedDescription, setEditedDescription] = useState(
    project.description ?? '',
  );

  const updateNameMutation = useUpdateProjectName();
  const updateDescriptionMutation = useUpdateProjectDescription();

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== project.name) {
      updateNameMutation.mutate(
        { projectId: project.id, name: editedName.trim() },
        { onSuccess: () => setIsEditNameOpen(false) },
      );
    } else {
      setIsEditNameOpen(false);
    }
  };

  const handleCloseName = () => {
    setEditedName(project.name);
    setIsEditNameOpen(false);
  };

  const handleSaveDescription = () => {
    if (editedDescription.trim() !== (project.description ?? '').trim()) {
      updateDescriptionMutation.mutate(
        { projectId: project.id, description: editedDescription.trim() },
        { onSuccess: () => setIsEditDescriptionOpen(false) },
      );
    } else {
      setIsEditDescriptionOpen(false);
    }
  };

  const handleCloseDescription = () => {
    setEditedDescription(project.description ?? '');
    setIsEditDescriptionOpen(false);
  };

  const { data: projectContractors } = useProjectContractors({
    projectId: project.id,
    type: project.type,
  });

  const { clientViewId, contractorViewId, isInternalSubproject } =
    useMemo(() => {
      return project.type === 'subproject_client'
        ? {
            clientViewId: project.id,
            contractorViewId:
              projectContractors?.[0]?.subproject_view_id ?? null,
            isInternalSubproject:
              project.org_id === projectContractors?.[0]?.contractor_orga_id,
          }
        : project.type === 'subproject_contractor'
          ? {
              clientViewId: project.parent_id,
              contractorViewId: project.id,
              isInternalSubproject: project.org_id === project.parent?.org_id,
            }
          : {
              clientViewId: null,
              contractorViewId: null,
              isInternalSubproject: false,
            };
    }, [project.type, projectContractors]);

  const [projectKpisQuery, derivativeKpisQuery] = useProjectKpisWithMetrics({
    projectId: project.id,
    projectType: project.type,
  });
  const isKpisPending =
    projectKpisQuery.isPending || derivativeKpisQuery.isPending;
  const kpis = [
    ...(projectKpisQuery.data?.filter(Boolean) || []),
    ...(derivativeKpisQuery.data?.filter(Boolean) || []),
  ];

  return (
    <>
      <ProjectDetailsHeader>
        <div className="flex w-full items-center justify-between gap-4">
          <Popover.Root
            open={isEditNameOpen}
            onOpenChange={(open) => {
              if (!open) handleCloseName();
            }}
          >
            <div className="flex items-center gap-2">
              <h1 className={'text-2xl font-medium'}>{project.name}</h1>
              <Popover.Trigger asChild>
                <button
                  onClick={() => {
                    setEditedName(project.name);
                    setIsEditNameOpen(true);
                  }}
                  className="rounded p-1 text-custom-grey transition-colors hover:bg-white/10 hover:text-white"
                  aria-label={t('edit_name')}
                >
                  <img src={EditIcon} alt="Edit" className="size-4" />
                </button>
              </Popover.Trigger>
            </div>
            <Popover.Portal>
              <Popover.Content
                side="bottom"
                align="start"
                sideOffset={8}
                className="z-50 w-[376px]"
              >
                <div
                  className="relative rounded-[16px] border-[0.5px] border-transparent backdrop-blur-md"
                  style={{
                    background:
                      'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), linear-gradient(135.632deg, rgba(68, 0, 155, 0.2) 0%, rgba(35, 112, 202, 0.2) 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                  }}
                >
                  <div className="rounded-[16px] bg-transparent p-[8px]">
                    {/* Title Container */}
                    <div className="mb-[8px] flex items-center justify-between px-[8px] pb-0 pl-[8px] pr-[6px] pt-[2px]">
                      <p className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#fefefe]">
                        {t('edit_project_name')}
                      </p>
                      <button
                        onClick={handleCloseName}
                        className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
                      >
                        <Cross2Icon className="size-4 text-white" />
                        <span className="sr-only">Close</span>
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="mb-[8px] h-[1px] w-full bg-white/50" />

                    {/* Content */}
                    <div className="space-y-[16px] px-[8px] pb-[8px]">
                      {/* Input Field */}
                      <div className="flex w-full items-center gap-[8px] overflow-clip rounded-[8px] border-[0.5px] border-[rgba(255,255,255,0.32)] bg-[rgba(255,255,255,0.05)] p-[8px] shadow-[inset_0px_20px_50px_0px_rgba(255,255,255,0.01)]">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          placeholder={t('project_name_placeholder')}
                          className="w-full bg-transparent text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#d9d9d9] placeholder:text-[#d9d9d9] focus:outline-none"
                          autoFocus
                        />
                      </div>

                      {/* Buttons */}
                      <div className="flex w-full gap-[12px]">
                        <button
                          type="button"
                          onClick={handleCloseName}
                          className="flex flex-1 items-center justify-center rounded-[8px] border-[0.5px] border-solid border-white px-[16px] py-[10px] text-[13px] font-[510] tracking-[-0.4px] text-white transition-colors hover:bg-white/10"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveName}
                          disabled={
                            updateNameMutation.isPending || !editedName.trim()
                          }
                          className="flex flex-1 items-center justify-center rounded-[8px] border-[0.5px] border-solid border-white px-[16px] py-[10px] text-[13px] font-[510] tracking-[-0.4px] text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                          style={{
                            backgroundImage:
                              'linear-gradient(105.141deg, rgb(161, 255, 128) 0.16086%, rgb(113, 178, 90) 147.39%)',
                          }}
                        >
                          {updateNameMutation.isPending ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            t('save')
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <CollapsibleTriggerContainer
              isExpanded={isPropertiesExpanded}
              onClick={() => toggleProperties(!isPropertiesExpanded)}
            >
              {t('properties')}
            </CollapsibleTriggerContainer>
            {clientViewId && contractorViewId && (
              <ProjectComments
                clientViewId={clientViewId}
                contractorViewId={contractorViewId}
                type={project.type}
                internalSubproject={isInternalSubproject}
              />
            )}
            <Button
              variant={'outline'}
              btnType={'secondary'}
              onClick={() => toggleNotes(!isNotesExpanded)}
              className="flex-shrink-0 rounded-[8px] border-[0.5px] border-[#d9d9d9] px-4 py-2.5 text-[13px] font-medium leading-normal"
            >
              {isNotesExpanded ? t('hide_notes') : t('show_notes')}
              <img src={EyeIcon} alt="eye" className="size-4 object-contain" />
            </Button>
          </div>
        </div>
        <Popover.Root
          open={isEditDescriptionOpen}
          onOpenChange={(open) => {
            if (!open) handleCloseDescription();
          }}
        >
          <div className="mt-3 flex max-w-3xl items-start gap-2">
            <p className="text-sm text-[#d9d9d9]">
              {project.description?.trim() ||
                t('project_description_placeholder')}
            </p>
            <Popover.Trigger asChild>
              <button
                onClick={() => {
                  setEditedDescription(project.description ?? '');
                  setIsEditDescriptionOpen(true);
                }}
                className="mt-0.5 shrink-0 rounded p-1 text-custom-grey transition-colors hover:bg-white/10 hover:text-white"
                aria-label={t('edit_description')}
              >
                <img src={EditIcon} alt="Edit" className="size-3.5" />
              </button>
            </Popover.Trigger>
          </div>
          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="start"
              sideOffset={8}
              className="z-50 w-[376px]"
            >
              <div
                className="relative rounded-[16px] border-[0.5px] border-transparent backdrop-blur-md"
                style={{
                  background:
                    'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), linear-gradient(135.632deg, rgba(68, 0, 155, 0.2) 0%, rgba(35, 112, 202, 0.2) 100%)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                }}
              >
                <div className="rounded-[16px] bg-transparent p-[8px]">
                  <div className="mb-[8px] flex items-center justify-between px-[8px] pb-0 pl-[8px] pr-[6px] pt-[2px]">
                    <p className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#fefefe]">
                      {t('edit_project_description')}
                    </p>
                    <button
                      onClick={handleCloseDescription}
                      className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
                    >
                      <Cross2Icon className="size-4 text-white" />
                      <span className="sr-only">Close</span>
                    </button>
                  </div>
                  <div className="mb-[8px] h-[1px] w-full bg-white/50" />
                  <div className="space-y-[16px] px-[8px] pb-[8px]">
                    <div className="flex w-full items-start gap-[8px] overflow-clip rounded-[8px] border-[0.5px] border-[rgba(255,255,255,0.32)] bg-[rgba(255,255,255,0.05)] p-[8px] shadow-[inset_0px_20px_50px_0px_rgba(255,255,255,0.01)]">
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder={t('project_description_placeholder')}
                        className="min-h-[80px] w-full resize-none bg-transparent text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#d9d9d9] placeholder:text-[#d9d9d9] focus:outline-none"
                        autoFocus
                      />
                    </div>
                    <div className="flex w-full gap-[12px]">
                      <button
                        type="button"
                        onClick={handleCloseDescription}
                        className="flex flex-1 items-center justify-center rounded-[8px] border-[0.5px] border-solid border-white px-[16px] py-[10px] text-[13px] font-[510] tracking-[-0.4px] text-white transition-colors hover:bg-white/10"
                      >
                        {t('cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveDescription}
                        disabled={updateDescriptionMutation.isPending}
                        className="flex flex-1 items-center justify-center rounded-[8px] border-[0.5px] border-solid border-white px-[16px] py-[10px] text-[13px] font-[510] tracking-[-0.4px] text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                        style={{
                          backgroundImage:
                            'linear-gradient(105.141deg, rgb(161, 255, 128) 0.16086%, rgb(113, 178, 90) 147.39%)',
                        }}
                      >
                        {updateDescriptionMutation.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          t('save')
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
        {(isPropertiesExpanded || isNotesExpanded) && (
          <>
            <div className="h-[39px]" />
            <div
              className={cn(
                'flex flex-col gap-3',
                isPropertiesExpanded &&
                  isNotesExpanded &&
                  'xl:flex-row xl:items-start',
              )}
            >
              {isPropertiesExpanded && (
                <h2
                  className="text-left text-[17px] font-normal leading-[22px] tracking-[-0.43px] text-[#d9d9d9] xl:flex-1"
                  style={{
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  {t('properties')}
                </h2>
              )}
              {isNotesExpanded && (
                <h2
                  className={cn(
                    'text-left text-[17px] font-normal leading-[22px] tracking-[-0.43px] text-[#d9d9d9]',
                    isPropertiesExpanded && 'xl:flex-1',
                  )}
                  style={{
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  {t('notes')}
                </h2>
              )}
            </div>
            <div className="h-3" />
          </>
        )}
        <div className="flex flex-col gap-3 xl:flex-row xl:items-stretch">
          {isPropertiesExpanded && (
            <div className={cn(isNotesExpanded ? 'flex-1' : 'w-full')}>
              <ProjectPropertiesMenu
                project={project}
                contractors={projectContractors ?? []}
                invitedContractors={
                  project.contractor_subproject_invitations ?? []
                }
                category={project.category ?? ''}
                subCategory={project.sub_category ?? ''}
                startDate={project.start_date}
                endDate={project.end_date}
                connectedProjectId={project.connected_project}
                isNotesExpanded={isNotesExpanded}
              />
            </div>
          )}
          {isNotesExpanded && (
            <div
              className={cn(isPropertiesExpanded ? 'flex flex-1' : 'w-full')}
            >
              <ProjectNotes
                project={project}
                noteType={activeTab}
                onNoteTypeChange={(type) => setActiveTab(type)}
                isPropertiesExpanded={isPropertiesExpanded}
              />
            </div>
          )}
        </div>
      </ProjectDetailsHeader>
      <ProjectDetailsKpis
        projectType={project.type}
        kpis={kpis}
        isKpisPending={isKpisPending}
        projectId={project.id}
      />

      {project.type === 'project' && (
        <ProjectDetailsSubProjects projectId={project.id} />
      )}
      <ProjectDetailsDocuments
        projectId={project.id}
        projectType={project.type}
        partnerName={projectContractors?.[0]?.partner_alias?.alias}
      />
      <ProjectDetailsTrackingReportHistory
        project={project}
        kpis={kpis
          .filter((kpi) => !isDerivativeKpi(kpi))
          .map((kpi) => ({
            ...kpi,
            description: null,
            owner_org_id: null,
          }))}
      />
      <ProjectDetailsCharts projectId={project.id} projectType={project.type} />
    </>
  );
};

const ProjectError = () => {
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });

  return <div>{t('could_not_fetch_project')}</div>;
};
